import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingMessage, setPendingMessage] = useState(null);

  const refresh = useCallback(async () => {
    // Only a definitive 401/403 means "not logged in". Transient network or
    // 5xx errors (e.g. backend restart) are retried and never destroy a
    // possibly-valid session — fixes unexpected logouts on refresh.
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        setLoading(false);
        return;
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          setUser(null);
          setLoading(false);
          return;
        }
        await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
      }
    }
    // Backend unreachable after retries: keep any existing user state rather
    // than forcing a logout; a later keepalive will resolve the truth.
    setUser((u) => u || null);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Session keepalive: slides the server-side session window every 10 minutes
  // while the app is open, so active users are never logged out mid-work.
  useEffect(() => {
    const t = setInterval(() => {
      api.get("/auth/me").then((r) => setUser(r.data)).catch((e) => {
        if (e?.response?.status === 401) setUser(null);
      });
    }, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // A truly expired/invalid session (401 from any API call) logs out ONCE,
  // cleanly, and redirects to login via the Protected route.
  useEffect(() => {
    const onUnauthorized = () => setUser(null);
    window.addEventListener("salesos:unauthorized", onUnauthorized);
    return () => window.removeEventListener("salesos:unauthorized", onUnauthorized);
  }, []);

  // SalesOS's own Google sign-in — a standard OAuth Authorization Code flow
  // against Google's own endpoints, using this org's own Client ID/Secret
  // (Administration -> Integrations -> Google Workspace). No Emergent
  // involvement: the backend redirects straight back here with a session
  // cookie already set, so there's no client-side token exchange step.
  const loginWithGoogle = async () => {
    try {
      const { data } = await api.get("/auth/google/login");
      window.location.href = data.authorization_url;
      return { ok: true };
    } catch (e) {
      const detail = e?.response?.data?.detail || "Google sign-in isn't available";
      return { ok: false, detail };
    }
  };

  const loginWithPassword = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      setPendingMessage(null);
      return { ok: true };
    } catch (e) {
      const detail = e?.response?.data?.detail || "Login failed";
      return { ok: false, detail };
    }
  };

  // LOCAL DEV ONLY: bypasses Google entirely via the backend's /auth/dev-login,
  // which itself 404s unless the server has LOCAL_DEV_LOGIN=true set. Never
  // reachable in a production build (see REACT_APP_ENABLE_DEV_LOGIN gate on
  // the button in LoginPage.jsx).
  // The whole body sits behind the build-time flag, so a production build (flag unset) contains neither the
  // function nor the "/auth/dev-login" path - backend/scripts/release_hygiene.py asserts that on the built bundle.
  const devLogin = process.env.REACT_APP_ENABLE_DEV_LOGIN === "true"
    ? async () => {
      try {
        const { data } = await api.post("/auth/dev-login");
        setUser(data);
        setPendingMessage(null);
        return { ok: true };
      } catch (e) {
        const detail = e?.response?.data?.detail || "Dev login failed";
        return { ok: false, detail };
      }
    }
    : async () => ({ ok: false, detail: "Not available" });

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
    }
  };

  const hasCap = (cap) => {
    const caps = user?.capabilities || [];
    if (caps.includes("*")) return true;
    if (caps.includes(cap)) return true;
    return caps.includes(`${cap.split(".")[0]}.*`);
  };

  // Explicit-only grants (Case exceptions): the exact string must be listed on the role. "*" and "prefix.*"
  // deliberately do NOT satisfy it - mirrors core.has_explicit_capability on the server.
  const hasExplicitCap = (cap) => (user?.capabilities || []).includes(cap);

  return (
    <AuthContext.Provider value={{ user, loading, pendingMessage, setPendingMessage, loginWithGoogle, loginWithPassword, devLogin, logout, hasCap, hasExplicitCap, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
